import { FC, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';

import { AuthProvider } from '../providers/authProvider';
import { SettingProvider } from '../providers/settingProvider';
import { AppProvider } from '../providers/appProvider';
import { HistoryProvider } from '../providers/historyProvider';

import Layout from '../layout/Layout';
import Home from '../pages/Home';
import LoadingPageComponent from '@/components/common/LoadingPage';
const MyProfilePage = lazy(() => import('../pages/MyProfilePage'));
const ManageContentPage = lazy(() => import('../pages/ManageContentPage'));
const ChatWidgetGenerator = lazy(() => import('../pages/ChatWidgetGenerator'));
const SandboxPage = lazy(() => import('../pages/SandboxPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const MySettingPage = lazy(() => import('../pages/MySettingPage'));
const BrandAgentPage = lazy(() => import('../pages/BrandAgentPage'));
const AgentSetup = lazy(() => import('@/components/agentBuilder/AgentSetup'));
const AppBuilder = lazy(() => import('@/components/app-builder/AppBuilder'));
const IntermediateAppPage = lazy(
  () => import('@/components/app-builder/app-file-manger/IntermediateAppPage')
);

// new store landing page
const LibraryPage = lazy(() =>
  import('../components/library/components/container/LibraryPage').then((m) => ({
    default: m.LibraryPage,
  }))
);

import ProtectedRoute from './ProtectedRoute';
const SkillBuilder = lazy(() => import('@/components/skill-builder/SkillBuilder'));
const PromptBuilder = lazy(() => import('@/components/prompt-builder/PromptBuilder'));

export const AppRoutes: FC = () => {
  const [user, setUser] = useState<AuthContextStore>();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleFocusOfSkipToContentAnchor = () => {
    if (linkRef.current) {
      linkRef.current.classList.add('skip-link-visible');
    }
  };

  const handleBlurOfSkipToContentAnchor = () => {
    if (linkRef.current) {
      linkRef.current.classList.remove('skip-link-visible');
    }
  };

  const handleClickOnSelectSkipToContent = () => {
    if (contentRef.current) {
      contentRef.current.focus();
      if (linkRef.current) {
        linkRef.current.classList.remove('skip-link-visible');
      }
    }
  };

  const handleKeyDownOnSkipToContent = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleClickOnSelectSkipToContent();
    }
  };
  const chatInputRef = useRef(null);

  return (
    <BrowserRouter>
      <AuthProvider user={user} setUser={setUser}>
        <AppProvider user={user}>
          <SettingProvider user={user}>
            <HistoryProvider user={user}>
              <>
                <a
                  href="#main-content"
                  ref={linkRef}
                  className="skip-link"
                  onFocus={handleFocusOfSkipToContentAnchor}
                  onBlur={handleBlurOfSkipToContentAnchor}
                  onClick={handleClickOnSelectSkipToContent}
                  onKeyDown={handleKeyDownOnSkipToContent}
                >
                  Skip to main content
                </a>
                <Layout chatInputRef={chatInputRef}>
                  <Routes>
                    <Route
                      path="/"
                      element={<Home contentRef={contentRef} chatInputRef={chatInputRef} />}
                    />
                    <Route
                      path="/myprofile"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <MyProfilePage contentRef={contentRef} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/mysettings"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <MySettingPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/mycontent"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <ManageContentPage contentRef={contentRef} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/generate"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <ChatWidgetGenerator user={user} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <NotificationsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/brand-agent"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <BrandAgentPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/sandbox"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingPageComponent />}>
                            <SandboxPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/session/:sessionid"
                      element={<Home contentRef={contentRef} chatInputRef={chatInputRef} />}
                    />
                    <Route
                      path="/app=:agentId"
                      element={<Home contentRef={contentRef} chatInputRef={chatInputRef} />}
                    />
                    <Route
                      path="/brand-configuration"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingPageComponent />}>
                            <AgentSetup contentRef={contentRef} />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingPageComponent />}>
                            <Dashboard />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/app-store"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <LibraryPage key="apps" entity="apps" />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/app-builder"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <AppBuilder contentRef={contentRef} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ai-app/"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <IntermediateAppPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/skill-builder"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <SkillBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/skill-builder/:skillId"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <SkillBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/prompt-builder"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <PromptBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/prompt-builder/:promptId"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <PromptBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/skill-builder"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <SkillBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/skill-builder/:skillId"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <SkillBuilder />
                        </Suspense>
                      }
                    />
                    <Route
                      path="*"
                      element={<Home contentRef={contentRef} chatInputRef={chatInputRef} />}
                    />

                    {/* Route for skills */}
                    <Route
                      path="/skill-store"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <LibraryPage key="skills" entity="skills" />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/prompt-store"
                      element={
                        <Suspense fallback={<LoadingPageComponent />}>
                          <LibraryPage key="prompts" entity="prompts" />
                        </Suspense>
                      }
                    />
                  </Routes>
                </Layout>
              </>
            </HistoryProvider>
          </SettingProvider>
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};
